import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { useFooterPages } from '@/ui/cms/useFooterPages';

export interface FooterProps {
  className?: string;
  /** Account logo from `DomainImages.Logo`; hidden when unavailable. */
  logoUrl?: string | null;
}

function FooterItem({
  href,
  label,
  external,
}: {
  href: string;
  label: string;
  external: boolean;
}) {
  const className = 'text-body-m text-text-secondary hover:text-text-primary';

  if (external) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={className}>
        {label}
      </a>
    );
  }

  return (
    <Link to={href} className={className}>
      {label}
    </Link>
  );
}

export function Footer({ className, logoUrl }: FooterProps) {
  const { t, i18n } = useTranslation();
  const { sections, socialLinks, policyLinks } = useFooterPages();
  const columnSections = sections.filter((section) => section.key !== 'responsibleGambling');
  const responsibleSection = sections.find((section) => section.key === 'responsibleGambling');

  const translateLabel = (label: string, translateKey?: string) => {
    const key = translateKey ?? label;
    if (i18n.exists(key, { ns: 'sports' })) {
      return t(key, { ns: 'sports', defaultValue: label });
    }
    return t(key, { defaultValue: label });
  };

  return (
    <footer
      className={cn(
        'bg-chrome-footer mt-auto flex flex-col gap-8 border-t border-border-subtle px-6 py-8 lg:px-10',
        className,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <span
          className="inline-flex h-12 w-12 items-center justify-center rounded-full border-2 border-[#f0c232] text-xl font-semibold text-[#f0c232]"
          aria-label="18 plus"
        >
          18+
        </span>
        {socialLinks.length > 0 && (
          <div
            className="flex items-center gap-2"
            aria-label={t('Social media', { defaultValue: 'Social media' })}
          >
            {socialLinks.map((social) => (
              <a
                key={social.id}
                href={social.href}
                target="_blank"
                rel="noreferrer"
                aria-label={social.label}
                className="inline-flex h-12 min-w-12 items-center justify-center rounded-lg border border-border-default bg-bg-surface px-3"
              >
                <img src={social.imageUrl} alt="" className="h-6 w-auto object-contain" />
              </a>
            ))}
          </div>
        )}
      </div>

      <nav
        aria-label={t('Footer pages', { defaultValue: 'Footer pages' })}
        className="grid grid-cols-2 gap-x-10 gap-y-8 lg:grid-cols-3 xl:grid-cols-6"
      >
        {columnSections.map((section) => (
          <section key={section.key} className="flex flex-col gap-3">
            <h2 className="text-label-m italic text-text-primary">
              {translateLabel(section.title, section.translateKey)}
            </h2>
            <div className="flex flex-col gap-2">
              {section.links.map((link) => (
                <FooterItem
                  key={`${section.key}-${link.id}`}
                  href={link.href}
                  label={translateLabel(link.label, link.translateKey)}
                  external={link.external}
                />
              ))}
            </div>
          </section>
        ))}
      </nav>

      {(responsibleSection || policyLinks.length > 0) && (
        <div className="flex flex-col gap-4 border-t border-border-subtle pt-6 lg:flex-row lg:items-start lg:justify-between">
          {responsibleSection && (
            <section className="flex flex-col gap-3">
              <h2 className="text-label-m italic text-text-primary">
                {translateLabel(responsibleSection.title, responsibleSection.translateKey)}
              </h2>
              <div className="flex flex-col gap-2">
                {responsibleSection.links.map((link) => (
                  <FooterItem
                    key={`responsibleGambling-${link.id}`}
                    href={link.href}
                    label={translateLabel(link.label, link.translateKey)}
                    external={link.external}
                  />
                ))}
              </div>
            </section>
          )}
          <nav
            aria-label={t('Footer policies', { defaultValue: 'Footer policies' })}
            className="flex flex-wrap gap-x-4 gap-y-2 lg:justify-end"
          >
            {policyLinks.map((link) => (
              <FooterItem
                key={link.id}
                href={link.href}
                label={translateLabel(link.label, link.translateKey)}
                external={link.external}
              />
            ))}
          </nav>
        </div>
      )}

      {logoUrl ? (
        <div className="flex items-center justify-center pt-2">
          <img src={logoUrl} alt="" className="h-8 w-auto max-w-[160px] object-contain opacity-95" />
        </div>
      ) : null}
    </footer>
  );
}
